import React, {useEffect, useMemo, useState} from "react"
import {useSessionCookie} from "../../hooks/useSessionCookie";
import {Alert, Box, Button, Card, Grid, Stack, Tab, tabClasses, TabList, Tabs, Typography} from "@mui/joy";
import {Bell, CreditCard, LogOutIcon, Paintbrush, Paintbrush2, Palette, SaveIcon, Shield, UserIcon} from "lucide-react";
import SettingsContext from "../../context/SettingsContext";
import AccountSettingsSection from "./AccountSettingsSection";
import {useSearchParams} from "react-router-dom";
import SecuritySettingsSection from "./SecuritySettingsSection";
import SubscriptionSettingsSection from "./SubscriptionSettingsSection";
import {useUITheme} from "../../hooks/useUITheme";
import ThemeSettingsSection from "./ThemeSettingsSection";
import {useSettings} from "../../hooks/useSettings";
import {useAuth} from "../../hooks/useAuth";
import {useTranslation} from "react-i18next";
import {formatTimeFromSeconds2} from "../../utils/formatTime";
import {User} from "../../models/user";
import {formatDate} from "../../utils/formatDate";
import {useLanguage} from "../../hooks/useLanguage";
import DeleteAccountDialog from "../../components/DeleteAccountDialog";


const Settings = () => {
    const auth = useAuth()
    const {userSession} = useSessionCookie()
    const user: User = userSession['currentUser']
    const uiTheme = useUITheme()
    const settings = useSettings()
    const language = useLanguage()
    const [searchParams, setSearchParams] = useSearchParams()
    const {t} = useTranslation()
    const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false)
    const sections = ["profile", "theme", "security", "subscription"]
    const handleSectionChange = (index: number) => {
        setSearchParams("?section=" + sections[index])
    }

    useEffect(() => {
        if (!searchParams.get("section"))
            setSearchParams("?section=profile")
    }, [])

    const saveChangesButton = <Button size={"lg"} onClick={settings.onSaveChanges} loading={settings.savingChanges}
                                      disabled={!settings.canSaveChanges} startDecorator={<SaveIcon/>}>
        {t("accountSettingsSaveChangesActionLabel")}
    </Button>

    // overflow stays visible on small screens so the save bar can stick to the top of the page's scroll area.
    return <Stack gap={3} padding={3} height={{md: "87dvh"}} overflow={{md: "hidden"}}>
        {/* Small screens: pinned just below the navbar so it's reachable without scrolling past the form. */}
        <Box display={{xs: "flex", md: "none"}} flexDirection={"column"}
             sx={{
                 position: "sticky",
                 // The page scroll area has a 2-unit top padding; cancel it so the bar meets the navbar.
                 top: theme => `calc(-1 * ${theme.spacing(2)})`,
                 zIndex: 10,
                 mx: -3,
                 mt: -3,
                 px: 3,
                 py: 1.5,
                 backdropFilter: uiTheme.sideBarProps.backdropFilter,
                 backgroundColor: uiTheme.sideBarProps.backgroundColor,
             }}>
            {saveChangesButton}
        </Box>

        <Stack gap={1}>
            <Typography level={"h2"}>
                {t("accountSettingsTitle")}
            </Typography>
            <Typography>
                {t("accountSettingsDescription")}
            </Typography>
        </Stack>

        <Tabs onChange={(event, value) => handleSectionChange(value as number)} aria-label="tabs"
              defaultValue={sections.findIndex(value => value === searchParams.get("section"))}
              sx={{bgcolor: 'transparent'}}>
            <TabList size={"lg"}
                     disableUnderline
                     sx={{
                         p: 0.5,
                         gap: 2,
                         borderRadius: 'xl',
                         bgcolor: 'transparent',
                         color: "white",
                         [`& .${tabClasses.root}`]: {
                             borderRadius: 13,
                             paddingX: 3,
                             color: "text.tertiary",
                             ":hover": {
                                 backgroundColor: uiTheme.navigationStyle.hoverBgColor,
                                 color: "initial"
                             }
                         },
                         [`& .${tabClasses.root}[aria-selected="true"]`]: {
                             boxShadow: 'sm',
                             color: "white",
                             background: uiTheme.navigationStyle['background'],
                             backgroundColor: uiTheme.navigationStyle.selectedBgColor,
                         },
                     }}
            >
                <Tab disableIndicator><UserIcon style={{color: "inherit"}}/> {t("accountSettingsTabTitle1")}</Tab>
                <Tab disableIndicator><Palette style={{color: "inherit"}}/> {t("accountSettingsTabTitle2")}</Tab>
                <Tab disableIndicator><Shield style={{color: "inherit"}}/> {t("accountSettingsTabTitle3")}</Tab>
                <Tab disableIndicator><CreditCard style={{color: "inherit"}}/> {t("accountSettingsTabTitle4")}</Tab>
            </TabList>
        </Tabs>

        <Grid container spacing={2} overflow={"auto"}>

            <Grid md={8} xs={12} className={"hide-scrollbar-y"} sx={{height: {md: "68dvh"}}}>
                {searchParams.get("section") === "profile" && <AccountSettingsSection/>}
                {searchParams.get("section") === "security" && <SecuritySettingsSection/>}
                {searchParams.get("section") === "subscription" && <SubscriptionSettingsSection/>}
                {searchParams.get("section") === "theme" && <ThemeSettingsSection/>}

            </Grid>

            <Grid md={4} xs={12}>
                <Stack gap={2}>
                    <Box display={{xs: "none", md: "flex"}} flexDirection={"column"}>
                        {saveChangesButton}
                    </Box>

                    <Card data-no-glow data-no-animation sx={{padding: 3}}>
                        <Typography level={"title-lg"}>
                            {t("accountInfoTitle")}
                        </Typography>

                        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                            <Typography>
                                {t("membershipInfoLabel")}
                            </Typography>

                            <Typography level={"title-sm"}>
                                {formatDate(userSession['currentUser']?.['createdAt'] as number, navigator.languages.find(value => value.substring(0, 2) === language.lang))}
                            </Typography>
                        </Stack>

                        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                            <Typography>
                                {t("totalTestsLabel")}
                            </Typography>

                            <Typography level={"title-sm"}>
                                {Object.keys(user?.testsCompleted || 0)?.reduce((previousValue, currentValue) => user.testsCompleted[currentValue] + previousValue, 0) || 0}
                            </Typography>
                        </Stack>

                        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                            <Typography>
                                {t("totalStudyTimeLabel")}
                            </Typography>

                            <Typography level={"title-sm"}>
                                {formatTimeFromSeconds2(user?.studyTime || 0)}
                            </Typography>
                        </Stack>
                    </Card>

                    <Card data-no-glow data-no-animation sx={{padding: 3}}>
                        <Typography level={"title-md"}>
                            {t("dangerZoneLabel")}
                        </Typography>

                        <Button onClick={auth.logout} loading={auth.loading} startDecorator={<LogOutIcon/>} size={"lg"}
                                variant={"plain"}
                                color={"danger"}>
                            {t("logoutActionLabel")}
                        </Button>

                        <Button color={"danger"} onClick={() => setDeleteAccountDialogOpen(true)}>
                            {t("deleteAccountActionLabel")}
                        </Button>

                    </Card>

                    <DeleteAccountDialog open={deleteAccountDialogOpen}
                                         user={user}
                                         loading={auth.loading}
                                         onClose={() => setDeleteAccountDialogOpen(false)}
                                         onConfirm={async confirmation => {
                                             if (await auth.deleteAccount(confirmation))
                                                 setDeleteAccountDialogOpen(false)
                                         }}/>
                </Stack>

            </Grid>

        </Grid>


    </Stack>
}


export default Settings